import context from '@truckdown/components-systems';
import { BannerViewModel, IBannerService } from '@truckdown/locations';
import { defineComponent, ref, Ref, computed, onMounted } from 'vue';

interface IBanner {
    id: string,
    imgUrl: string,
    clickUrl: string,
    leftPosition: number;
    style: any
}

interface IData {
    width: Ref<number>,
    offset: Ref<number>
}

const getBannerPosition = function (pos: number, total: number) {
    if (pos == 0) {
        return 0;
    }
    let numToRight = Math.ceil((total - 1) / 2);
    let numToLeft = Math.floor((total - 1) / 2);
    if (pos <= numToRight) {
        return pos;
    } else {
        return ((pos - numToRight) - 1) - numToLeft;
    }
}

const getUrl = function (path: string) {
    return context.apis['main'].getUrl(path);
}

function arrayRotate(arr: BannerViewModel[], reverse: boolean) {
    if (reverse) {
        let popped = arr.pop();
        if (popped) {
            arr.unshift(popped);
        }
    }
    else
    {
        let shifted = arr.shift();
        if (shifted) {
            arr.push(shifted);
        }
    }
    return arr;
}

const getBannerCopy = function (props: any, data: IData): BannerViewModel[] {
    let bannerCopy: BannerViewModel[] = [];
    (props.banners as BannerViewModel[]).forEach(val => bannerCopy.push(val));

    let offsetValue = data.offset.value;
    while (offsetValue > bannerCopy.length) {
        offsetValue = offsetValue - bannerCopy.length;
    }
    while (offsetValue < -bannerCopy.length) {
        offsetValue = offsetValue + bannerCopy.length;
    }

    let reverse = offsetValue < 0;
    offsetValue = Math.abs(offsetValue);
    for (let i = 0; i < offsetValue; i++) {
        arrayRotate(bannerCopy, reverse);
    }
    return bannerCopy;
}

const getBanners = function (props: any, data: IData) {
    let bannerCopy = getBannerCopy(props, data);

    return bannerCopy
        .map(function (b: BannerViewModel, idx: number, array: BannerViewModel[]): IBanner {
            let path = b.id + '/' + b.regionCode + '/' + b.services.join('_');
            
            let leftPosition = getBannerPosition(idx, array.length);
            let maxWidth = Math.min(480, document.documentElement.clientWidth);

            return {
                id: b.id,
                imgUrl: getUrl('/banners/image/' + path),
                clickUrl: getUrl('/banners/click/' + path),
                leftPosition: leftPosition * maxWidth,
                style: {
                    left: (leftPosition * maxWidth) + 'px'
                }
            } as IBanner;
        });
}

const getData = function (props: any) {
    return {
        width: ref<number>(document.documentElement.clientWidth),
        offset: ref<number>(0)
    }
}

const getComputed = function (props: any, data: IData) {
    return {
        sliderStyle: computed(() => {
            let maxWidth = Math.min(480, document.documentElement.clientWidth);

            let transform = (data.width.value / 2) - (maxWidth / 2);
            return 'transform: translateX(' + transform + 'px);';
        }),
        models: computed(() => {
            return getBanners(props, data);
        })
    };
}

const getMethods = function (data: IData) {
    return {
        onResize: function (ev: Event) {
            data.width.value = document.documentElement.clientWidth;
        },
        next: function (ev: Event) {
            data.offset.value = data.offset.value + 1;
        },
        previous: function (ev: Event) {
            data.offset.value = data.offset.value - 1;
        }
    };
}

export default defineComponent({
    props: {
        banners: {
            type: Array as () => BannerViewModel[],
            required: true
        }
    },
    emits: [],
    setup: function (props, ctx) {
        let data = getData(props);
        let computed = getComputed(props, data);
        let methods = getMethods(data);
        
        onMounted(() => {
            window.addEventListener('resize', methods.onResize);
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});
